import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowForwardIos as ArrowForwardIosIcon,
  Devices as DevicesIcon,
  FileDownload as FileDownloadIcon,
  PeopleOutline as PeopleIcon,
  Search as SearchIcon,
  StorefrontOutlined as StorefrontIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { LineChart, Line } from 'recharts';
import DateRangePopover from '../../Components/GatewayContagem/DateRangePopover';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import StatCard from '../../Components/GatewayContagem/StatCard';
import LoadingSkeleton from '../../Components/GatewayContagem/LoadingSkeleton';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import useCountings from '../../Context/Hooks/useCountings';
import { countingAPI } from '../../API/countingAPI';
import { storeAPI } from '../../API/storeAPI';
import { fmtNum, TABLE_HEAD_ROW_SX, toUtcStartOfDay, toUtcEndOfDay } from '../../Utils/format';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  operational: { color: '#2E9E5B', bg: '#E8F5E9' },
  offline: { color: '#C62828', bg: '#FFEBEE' },
  warning: { color: '#E65100', bg: '#FFF3E0' },
};

const defaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  d.setHours(0, 0, 0, 0);
  return d;
};

const defaultEndDate = () => new Date();

const toISODate = (d) => {
  if (!d) {
    return '';
  }
  if (typeof d === 'string' && d.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(d)) {
    return d.substring(0, 10);
  }
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const PERIOD_LABELS = {
  daily: { current: 'today', previous: 'yesterday', total: 'sevenDays', tableTotal: 'sevenDays' },
  hourly: { current: 'last24h', previous: 'previous24h', total: 'last24hTotal', tableTotal: 'last24hTotal' },
  weekly: { current: 'thisWeek', previous: 'lastWeek', total: 'sevenWeeks', tableTotal: 'sevenWeeks' },
  monthly: { current: 'thisMonth', previous: 'lastMonth', total: 'sevenMonths', tableTotal: 'sevenMonths' },
  custom: { current: 'inPeriod', previous: 'dailyAvg', total: 'dailyAvg', tableTotal: 'total' },
};

const weekKeyOf = (dateStr) => {
  const d = new Date(dateStr);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dow);
  return toISODate(d);
};

const monthKeyOf = (dateStr) => {
  if (!dateStr) {
    return '';
  }
  const d = typeof dateStr === 'string' && dateStr.length === 10 ? new Date(`${dateStr}T00:00:00`) : new Date(dateStr);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}`;
};

// Sums totalInCount per bucket (keyed by keyFn), returns totals sorted ascending by bucket key
const bucketTotals = (sortedDays, keyFn) => {
  const map = new Map();
  sortedDays.forEach((item) => {
    const k = keyFn(item.date);
    map.set(k, (map.get(k) ?? 0) + item.totalInCount);
  });
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, total]) => total);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

// eslint-disable-next-line react/prop-types
const StatusBadge = ({ status }) => {
  const { t } = useTranslation('listings');
  const { color, bg } = STATUS_CONFIG[status] ?? STATUS_CONFIG.offline;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10, bgcolor: bg }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color, fontWeight: 600, lineHeight: 1 }}>
        {t(`status.${status}`)}
      </Typography>
    </Box>
  );
};

// eslint-disable-next-line react/prop-types
const VariationBadge = ({ pct }) => {
  if (pct === null || pct === undefined || Number.isNaN(pct)) {
    return <Typography variant="caption" sx={{ color: 'text.disabled', px: 1 }}>—</Typography>;
  }
  const isUp = pct >= 0;
  const color = isUp ? '#2E9E5B' : '#C62828';
  const bg = isUp ? '#E8F5E9' : '#FFEBEE';
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.5, borderRadius: 1, bgcolor: bg }}>
      <Typography variant="caption" sx={{ color, fontWeight: 600, lineHeight: 1 }}>
        {isUp ? '↑' : '↓'} {isUp ? '+' : ''}{pct}%
      </Typography>
    </Box>
  );
};

// eslint-disable-next-line react/prop-types
const Sparkline = ({ data }) => (
  <LineChart width={100} height={36} data={data} style={{ overflow: 'visible' }}>
    <Line type="monotone" dataKey="v" stroke="#1B75BB" dot={false} strokeWidth={1.5} isAnimationActive={false} />
  </LineChart>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const ContagensPage = () => {
  const { t } = useTranslation('listings');
  const navigate = useNavigate();

  const { selectedClient } = useGatewayContagem();
  const { dailySummary, totalDailyCount, isLoading, loadDailySummary } = useCountings();

  const [timePeriod, setTimePeriod] = useState('daily');
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [hourlyData, setHourlyData] = useState({});
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [clientStores, setClientStores] = useState([]);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Debounce search query to avoid excessive API requests
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const isSearchLoading = search !== debouncedSearch;
  const isTableLoading = isLoading || isSearchLoading || hourlyLoading;

  // Fetch full store list for selected client to populate filter dropdowns independently
  useEffect(() => {
    if (!selectedClient?.id) {
      setClientStores([]);
      return;
    }
    storeAPI.getByClient(selectedClient.id).then(res => {
      const list = Array.isArray(res?.value) ? res.value : [];
      setClientStores(list);
    }).catch(() => setClientStores([]));
  }, [selectedClient?.id]);

  // Reset filters and page when client changes
  useEffect(() => {
    setSelectedStoreIds([]);
    setSelectedCities([]);
    setSelectedIds([]);
    setPage(0);
  }, [selectedClient?.id]);

  // Reset page when search or filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, selectedStoreIds.join(','), selectedCities.join(','), startDate, endDate, timePeriod]);

  // Reload when client, date range, search, filters or page change (100% Server-side filtering & pagination)
  const refresh = useCallback(() => {
    if (!selectedClient?.id) {
      return;
    }
    loadDailySummary(
      selectedClient.id,
      toUtcStartOfDay(startDate),
      toUtcEndOfDay(endDate),
      null,
      selectedStoreIds.length > 0 ? selectedStoreIds : null,
      debouncedSearch || null,
      selectedCities.length > 0 ? selectedCities : null,
      page + 1,
      rowsPerPage
    );
  }, [selectedClient?.id, startDate, endDate, selectedStoreIds, selectedCities, debouncedSearch, page, rowsPerPage, loadDailySummary]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Build per-store rows from dailySummary, bucketed by the selected timePeriod ──
  const allRows = useMemo(() => {
    if (!dailySummary.length) {
      return [];
    }

    const endDateStr = toISODate(endDate);
    const yesterdayStr = (() => {
      const d = new Date(endDate);
      d.setDate(d.getDate() - 1);
      return toISODate(d);
    })();

    // Group daily entries by storeId
    const byStore = {};
    if (Array.isArray(clientStores)) {
      let storesToSeed = clientStores;
      if (selectedStoreIds.length > 0) {
        storesToSeed = storesToSeed.filter(s => selectedStoreIds.includes(s.id));
      }
      if (selectedCities.length > 0) {
        storesToSeed = storesToSeed.filter(s => selectedCities.includes(s.city));
      }
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase().trim();
        storesToSeed = storesToSeed.filter(s => ((s.name && s.name.toLowerCase().includes(q)) || (s.externalId && s.externalId.toLowerCase().includes(q)) || (s.city && s.city.toLowerCase().includes(q))));
      }
      storesToSeed.forEach(s => {
        if (!s.id) {
          return;
        }
        byStore[s.id] = {
          storeId: s.id,
          storeName: s.name || '',
          storeExternalId: s.externalId ?? '',
          city: s.city || '',
          deviceStatus: (s.devicesOfflineCount > 0 ? 'offline' : (s.devicesWarningCount > 0 ? 'warning' : 'operational')),
          deviceCount: s.devicesCount ?? 0,
          days: [],
        };
      });
    }

    dailySummary.forEach(item => {
      const sid = item.storeId;
      const itemCity = item.city || item.City || '';
      if (!byStore[sid]) {
        byStore[sid] = {
          storeId: sid,
          storeName: item.storeName || '',
          storeExternalId: item.storeExternalId ?? '',
          city: itemCity,
          deviceStatus: item.deviceStatus ?? 'operational',
          days: [],
        };
      }
      byStore[sid].days.push(item);
      // Keep the most recent non-zero deviceCount, status, and non-empty city
      if (item.deviceCount > 0) {
        byStore[sid].deviceCount = item.deviceCount;
      }
      if (item.deviceStatus) {
        byStore[sid].deviceStatus = item.deviceStatus;
      }
      if (!byStore[sid].city && itemCity) {
        byStore[sid].city = itemCity;
      }
    });

    return Object.values(byStore).map(store => {
      // Aggregate duplicates (multiple devices) by date
      const aggregatedByDate = {};
      store.days.forEach(item => {
        const k = toISODate(item.date);
        if (!aggregatedByDate[k]) {
          aggregatedByDate[k] = {
            date: item.date,
            totalInCount: 0,
            totalOutCount: 0,
          };
        }
        aggregatedByDate[k].totalInCount += item.totalInCount;
        aggregatedByDate[k].totalOutCount += item.totalOutCount;
      });
      const sorted = Object.values(aggregatedByDate).sort((a, b) => new Date(a.date) - new Date(b.date));

      let visitorsToday;
      let visitorsYesterday;
      let trendData;
      let variation = 0;
      let visitors7Days;

      if (timePeriod === 'weekly') {
        const totals = bucketTotals(sorted, weekKeyOf);
        visitorsToday = totals[totals.length - 1] ?? 0;
        visitorsYesterday = totals[totals.length - 2] ?? 0;
        trendData = totals.slice(-7).map(v => ({ v }));
        variation = visitorsYesterday > 0
          ? Math.round(((visitorsToday - visitorsYesterday) / visitorsYesterday) * 100)
          : 0;
        visitors7Days = trendData.reduce((s, d) => s + d.v, 0);
      } else if (timePeriod === 'monthly') {
        const totals = bucketTotals(sorted, monthKeyOf);
        visitorsToday = totals[totals.length - 1] ?? 0;
        visitorsYesterday = totals[totals.length - 2] ?? 0;
        trendData = totals.slice(-7).map(v => ({ v }));
        variation = visitorsYesterday > 0
          ? Math.round(((visitorsToday - visitorsYesterday) / visitorsYesterday) * 100)
          : 0;
        visitors7Days = trendData.reduce((s, d) => s + d.v, 0);
      } else if (timePeriod === 'custom') {
        const startDateStr = toISODate(startDate);
        const endDateStr = toISODate(endDate);
        const inPeriodDays = sorted.filter(d => {
          const k = toISODate(d.date);
          return (!startDateStr || k >= startDateStr) && (!endDateStr || k <= endDateStr);
        });
        const periodSum = inPeriodDays.reduce((s, d) => s + d.totalInCount, 0);
        const isSingleDay = startDateStr === endDateStr;

        if (isSingleDay) {
          const dayItem = inPeriodDays[0];
          const yesterdayStr = (() => {
            const d = new Date(startDate);
            d.setDate(d.getDate() - 1);
            return toISODate(d);
          })();
          const yesterdayItem = sorted.find(d => toISODate(d.date) === yesterdayStr);
          visitorsToday = dayItem?.totalInCount ?? 0;
          visitorsYesterday = yesterdayItem?.totalInCount ?? 0;
          trendData = inPeriodDays.map(d => ({ v: d.totalInCount }));
          variation = (yesterdayItem && yesterdayItem.totalInCount > 0)
            ? Math.round(((visitorsToday - visitorsYesterday) / visitorsYesterday) * 100)
            : null;
          visitors7Days = visitorsToday;
        } else {
          const daysCount = Math.max(1, Math.round((new Date(endDateStr) - new Date(startDateStr)) / 86400000) + 1);
          visitorsToday = periodSum;
          visitorsYesterday = Math.round(periodSum / daysCount);
          trendData = inPeriodDays.map(d => ({ v: d.totalInCount }));
          if (inPeriodDays.length >= 2) {
            const mid = Math.floor(inPeriodDays.length / 2);
            const firstHalf = inPeriodDays.slice(0, mid).reduce((s, d) => s + d.totalInCount, 0);
            const secondHalf = inPeriodDays.slice(mid).reduce((s, d) => s + d.totalInCount, 0);
            variation = firstHalf > 0
              ? Math.round(((secondHalf - firstHalf) / firstHalf) * 100)
              : null;
          } else {
            variation = null;
          }
          visitors7Days = periodSum;
        }
      } else {
        // 'daily' and 'hourly' (hourly is overridden below once true hourly data loads)
        const todayItem = sorted.find(d => toISODate(d.date) === endDateStr);
        const yesterdayItem = sorted.find(d => toISODate(d.date) === yesterdayStr);
        visitorsToday = todayItem?.totalInCount ?? 0;
        visitorsYesterday = yesterdayItem?.totalInCount ?? 0;
        trendData = sorted.slice(-7).map(d => ({ v: d.totalInCount }));
        variation = visitorsYesterday > 0
          ? Math.round(((visitorsToday - visitorsYesterday) / visitorsYesterday) * 100)
          : 0;
        visitors7Days = trendData.reduce((s, d) => s + d.v, 0);
      }

      return {
        storeId: store.storeId,
        storeName: store.storeName,
        storeExternalId: store.storeExternalId,
        city: store.city,
        visitorsToday,
        visitorsYesterday,
        variation,
        visitors7Days,
        trendData,
        deviceCount: store.deviceCount ?? 0,
        status: store.deviceStatus,
      };
    });
  }, [dailySummary, clientStores, startDate, endDate, timePeriod, selectedStoreIds, selectedCities, debouncedSearch]);

  // ── True hourly granularity needs per-store backend aggregates (dailySummary has day-grain only) ──
  useEffect(() => {
    if (timePeriod !== 'hourly' || !selectedClient?.id || allRows.length === 0) {
      setHourlyData({});
      setHourlyLoading(false);
      return undefined;
    }

    let cancelled = false;
    setHourlyLoading(true);

    Promise.all(allRows.map(row => countingAPI
      .getAggregates(selectedClient.id, toUtcStartOfDay(startDate), toUtcEndOfDay(endDate), 'HOUR', row.storeId)
      .then(res => ({ storeId: row.storeId, points: res && !res.error ? (res.value || []) : [] }))))
      .then(results => {
        if (cancelled) {
          return;
        }
        const map = {};
        results.forEach(({ storeId, points }) => {
          const sorted = [...points].sort((a, b) => new Date(a.period) - new Date(b.period));
          const last24 = sorted.slice(-24);
          const prev24 = sorted.slice(-48, -24);
          const current = last24.reduce((s, p) => s + p.totalInCount, 0);
          const previous = prev24.reduce((s, p) => s + p.totalInCount, 0);
          const variation = previous > 0 ? Math.round(((current - previous) / previous) * 100) : 0;
          map[storeId] = { current, previous, variation, trendData: last24.map(p => ({ v: p.totalInCount })) };
        });
        setHourlyData(map);
        setHourlyLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setHourlyLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [timePeriod, selectedClient?.id, allRows, startDate, endDate]);

  // ── Filter options derived from client stores or data ────────────────────
  const stores = useMemo(() => {
    if (clientStores.length > 0) {
      return clientStores
        .map(s => ({ id: s.id, name: s.name, city: s.city }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return [...new Map(allRows.map(r => [r.storeId, { id: r.storeId, name: r.storeName, city: r.city }])).values()]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [clientStores, allRows]);

  const cities = useMemo(() => {
    if (clientStores.length > 0) {
      return [...new Set(clientStores.map(s => s.city).filter(Boolean))].sort();
    }
    return [...new Set(allRows.map(r => r.city).filter(Boolean))].sort();
  }, [clientStores, allRows]);

  // Overlay true per-hour backend data on top of the day-grain fallback once it has loaded
  const displayRows = useMemo(() => {
    if (timePeriod !== 'hourly') {
      return allRows;
    }
    return allRows.map(r => {
      const h = hourlyData[r.storeId];
      if (!h) {
        return r;
      }
      return {
        ...r,
        visitorsToday: h.current,
        visitorsYesterday: h.previous,
        variation: h.variation,
        trendData: h.trendData,
        visitors7Days: h.current,
      };
    });
  }, [allRows, timePeriod, hourlyData]);

  const isCustomMultiDay = timePeriod === 'custom' && toISODate(startDate) !== toISODate(endDate);

  const periodLabels = useMemo(() => {
    if (timePeriod === 'custom') {
      return isCustomMultiDay
        ? { current: 'inPeriod', previous: 'dailyAvg', total: 'dailyAvg', tableTotal: 'total' }
        : { current: 'selectedDay', previous: 'previousDay', total: 'total', tableTotal: 'total' };
    }
    return PERIOD_LABELS[timePeriod] ?? PERIOD_LABELS.daily;
  }, [timePeriod, isCustomMultiDay]);

  const selectedRows = useMemo(() => {
    if (selectedIds.length === 0) {
      return displayRows;
    }
    return displayRows.filter(r => selectedIds.includes(r.storeId));
  }, [displayRows, selectedIds]);

  const summary = useMemo(() => {
    const totalInPeriod = selectedRows.reduce((s, r) => s + r.visitorsToday, 0);
    const startDateStr = toISODate(startDate);
    const endDateStr = toISODate(endDate);
    const daysCount = Math.max(1, Math.round((new Date(endDateStr) - new Date(startDateStr)) / 86400000) + 1);
    const dailyAvg = Math.round(totalInPeriod / daysCount);

    return {
      selectedStores: selectedIds.length === 0 ? displayRows.length : selectedIds.length,
      totalStores: clientStores.length > 0 ? clientStores.length : (totalDailyCount || allRows.length),
      visitorsToday: totalInPeriod,
      visitorsWeek: isCustomMultiDay ? dailyAvg : selectedRows.reduce((s, r) => s + r.visitors7Days, 0),
      devices: selectedRows.reduce((s, r) => s + r.deviceCount, 0),
    };
  }, [displayRows.length, selectedRows, selectedIds.length, clientStores.length, totalDailyCount, allRows.length, timePeriod, isCustomMultiDay, startDate, endDate]);

  // ── Checkbox logic ─────────────────────────────────────────────────────────
  const allSelected = displayRows.length > 0 && displayRows.every(r => selectedIds.includes(r.storeId));
  const someSelected = displayRows.some(r => selectedIds.includes(r.storeId)) && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(ids => ids.filter(id => !displayRows.some(r => r.storeId === id)));
    } else {
      setSelectedIds(ids => [...new Set([...ids, ...displayRows.map(r => r.storeId)])]);
    }
  };

  const toggleRow = (id) => {
    setSelectedIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]));
  };

  const isDataLoading = !selectedClient?.id || isLoading || (timePeriod === 'hourly' && hourlyLoading);

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ── */}
      <PageHeader title={t('title')} subtitle={t('subtitle')}>
        <ToggleButtonGroup
          value={timePeriod}
          exclusive
          onChange={(_, v) => {
            if (!v) {
              return;
            }
            setTimePeriod(v);
            if (v === 'daily') {
              setStartDate(defaultStartDate());
              setEndDate(defaultEndDate());
            } else if (v === 'weekly') {
              const d = new Date();
              d.setDate(d.getDate() - 48);
              d.setHours(0, 0, 0, 0);
              setStartDate(d);
              setEndDate(defaultEndDate());
            } else if (v === 'monthly') {
              const d = new Date();
              d.setMonth(d.getMonth() - 6);
              d.setDate(1);
              d.setHours(0, 0, 0, 0);
              setStartDate(d);
              setEndDate(defaultEndDate());
            } else if (v === 'hourly') {
              setStartDate(defaultStartDate());
              setEndDate(defaultEndDate());
            }
          }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.5,
              py: 0.5,
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'none',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.secondary',
              '&.Mui-selected': {
                bgcolor: 'primary.dark',
                color: '#fff',
                borderColor: 'primary.dark',
                '&:hover': {
                  bgcolor: 'primary.main',
                },
              },
            },
          }}
        >
          <ToggleButton value="daily">{t('timePeriod.daily')}</ToggleButton>
          <ToggleButton value="hourly">{t('timePeriod.hourly')}</ToggleButton>
          <ToggleButton value="weekly">{t('timePeriod.weekly')}</ToggleButton>
          <ToggleButton value="monthly">{t('timePeriod.monthly')}</ToggleButton>
          <ToggleButton value="custom">{t('timePeriod.custom')}</ToggleButton>
        </ToggleButtonGroup>

        <DateRangePopover
          startDate={startDate}
          endDate={endDate}
          onStartChange={d => {
            if (d) {
              const s = new Date(d);
              s.setHours(0, 0, 0, 0);
              setStartDate(s);
              setTimePeriod('custom');
            }
          }}
          onEndChange={d => {
            if (d) {
              const e = new Date(d);
              e.setHours(23, 59, 59, 999);
              setEndDate(e);
              setTimePeriod('custom');
            }
          }}
          startLabel={t('datePicker.start')}
          endLabel={t('datePicker.end')}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<FileDownloadIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
          onClick={() => navigate('/Gateway/Export')}
        >
          {t('actions.exportExcel')}
        </Button>
      </PageHeader>

      {/* ── KPI Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<StorefrontIcon fontSize="inherit" />}
            title={t('kpi.selectedStores')}
            value={fmtNum(summary.selectedStores)}
            subtitle={`${t('kpi.of')} ${fmtNum(summary.totalStores)}`}
            loading={isDataLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PeopleIcon fontSize="inherit" />}
            title={t(`table.${periodLabels.current}`)}
            value={fmtNum(summary.visitorsToday)}
            loading={isDataLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingUpIcon fontSize="inherit" />}
            title={t(`table.${periodLabels.total}`)}
            value={fmtNum(summary.visitorsWeek)}
            loading={isDataLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<DevicesIcon fontSize="inherit" />}
            title={t('kpi.devices')}
            value={summary.devices}
            loading={isDataLoading}
          />
        </Grid>
      </Grid>

      {/* ── Table card ── */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        }}
      >
        {/* ── Filter bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder={t('filters.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 220,
                '& .MuiInputBase-root': { fontSize: 13 },
                '& .MuiOutlinedInput-root fieldset': { borderColor: 'divider' },
              }}
            />
            <Select
              size="small"
              multiple
              value={selectedStoreIds}
              onChange={e => {
                const val = e.target.value;
                setSelectedStoreIds(typeof val === 'string' ? val.split(',') : val);
              }}
              displayEmpty
              renderValue={selected => {
                if (selected.length === 0) {
                  return <span style={{ opacity: 0.55 }}>{t('filters.allStores')}</span>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(id => {
                      const store = stores.find(s => s.id === id);
                      return <Chip key={id} label={store?.name || id} size="small" sx={{ height: 22, fontSize: '0.75rem' }} />;
                    })}
                  </Box>
                );
              }}
              sx={{ fontSize: 13, minWidth: 180, maxWidth: 280, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}
            >
              {stores.map(s => (
                <MenuItem key={s.id} value={s.id} sx={{ fontSize: 13 }}>
                  <Checkbox checked={selectedStoreIds.includes(s.id)} size="small" />
                  <ListItemText primary={s.name} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              multiple
              value={selectedCities}
              onChange={e => {
                const val = e.target.value;
                setSelectedCities(typeof val === 'string' ? val.split(',') : val);
              }}
              displayEmpty
              renderValue={selected => {
                if (selected.length === 0) {
                  return <span style={{ opacity: 0.55 }}>{t('filters.allCities')}</span>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(c => (
                      <Chip key={c} label={c} size="small" sx={{ height: 22, fontSize: '0.75rem' }} />
                    ))}
                  </Box>
                );
              }}
              sx={{ fontSize: 13, minWidth: 160, maxWidth: 260, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}
            >
              {cities.map(c => (
                <MenuItem key={c} value={c} sx={{ fontSize: 13 }}>
                  <Checkbox checked={selectedCities.includes(c)} size="small" />
                  <ListItemText primary={c} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>
            {(search || selectedStoreIds.length > 0 || selectedCities.length > 0) && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setSearch('');
                  setSelectedStoreIds([]);
                  setSelectedCities([]);
                }}
                sx={{
                  textTransform: 'none',
                  color: 'primary.main',
                  fontSize: 13,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                }}
              >
                {t('filters.clearFilters', { defaultValue: 'Limpar Filtros' })}
              </Button>
            )}
          </Stack>

          <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {t('filters.results', { count: isTableLoading ? 0 : (totalDailyCount || displayRows.length) })}
          </Typography>
        </Box>

        {/* ── Table ── */}
        <LoadingSkeleton loading={isDataLoading} preset="table" height={360} sx={{ borderRadius: 0 }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={TABLE_HEAD_ROW_SX}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
                  </TableCell>
                  <TableCell>{t('table.store')}</TableCell>
                  <TableCell>{t('table.city')}</TableCell>
                  <TableCell align="right">{t(`table.${periodLabels.current}`)}</TableCell>
                  <TableCell align="right">{t(`table.${periodLabels.previous}`)}</TableCell>
                  <TableCell>{t('table.variation')}</TableCell>
                  <TableCell align="right">{t(`table.${periodLabels.tableTotal || periodLabels.total}`)}</TableCell>
                  <TableCell>{t('table.trend')}</TableCell>
                  <TableCell align="right">{t('table.devices')}</TableCell>
                  <TableCell>{t('table.status')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {displayRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 6, border: 'none' }}>
                      <Typography variant="body2" color="text.secondary">
                        {!selectedClient ? t('table.selectClient') : t('table.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayRows.map(row => (
                  <TableRow key={row.storeId} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(row.storeId)}
                        onChange={() => toggleRow(row.storeId)}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
                        {row.storeName || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.storeExternalId}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{row.city}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>{fmtNum(row.visitorsToday)}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2">{fmtNum(row.visitorsYesterday)}</Typography>
                    </TableCell>

                    <TableCell>
                      <VariationBadge pct={row.variation} />
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2">{fmtNum(row.visitors7Days)}</Typography>
                    </TableCell>

                    <TableCell sx={{ py: 0 }}>
                      <Sparkline data={row.trendData} />
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2">{row.deviceCount}</Typography>
                    </TableCell>

                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>

                    <TableCell sx={{ pr: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/Gateway/Stores/${row.storeId}`, { state: { from: 'countings' } })}
                        sx={{ color: 'text.disabled' }}
                      >
                        <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </LoadingSkeleton>

        <TablePagination
          component="div"
          count={totalDailyCount || displayRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          showFirstButton
          showLastButton
          labelRowsPerPage={t('common:pagination.rowsPerPage', { defaultValue: 'Linhas por página:' })}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of', { defaultValue: 'de' })} ${count}`}
          sx={{ borderTop: '1px solid', borderColor: 'divider', fontSize: 13 }}
        />
      </Box>
    </Box>
  );
};

export default ContagensPage;
